import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PropertyCard } from "@/components/PropertyCard";
import { useLanguage } from "@/context/LanguageContext";
import { neighborhoods } from "@/data/neighborhoods";
import { useListings } from "@/hooks/useListings";

export default function NeighborhoodDetail() {
  const { slug } = useParams();
  const { lang, t } = useLanguage();
  const { listings } = useListings();
  const n = neighborhoods.find((x) => x.slug === slug);
  if (!n) return <Navigate to="/neighborhoods" replace />;

  const localListings = listings.filter((l) => l.neighborhoodSlug === slug);

  return (
    <main data-testid={`neighborhood-detail-${slug}`}>
      <Seo
        title={`${n.name} Real Estate — Homes & Lifestyle | Carlos Vindel Realty`}
        siteName="Carlos Vindel Realty"
        description={n.description.en}
      />

      <section className="relative h-[70vh] min-h-[480px] overflow-hidden bg-ink">
        <img src={n.image} alt={`${n.name}, South Florida`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/30" />
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-ink/60 to-transparent" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-end pb-16">
          <Reveal>
            <Link to="/neighborhoods" data-testid="neighborhood-back-link" className="inline-flex items-center gap-2 text-cream/60 hover:text-gold text-xs uppercase tracking-[0.25em] font-semibold transition-colors mb-6">
              <ArrowLeft className="w-3.5 h-3.5" /> {t.neighborhoodsPage.back}
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[11px] uppercase tracking-[0.3em] text-gold font-semibold">{n.tagline[lang]}</p>
            <h1 className="mt-3 font-serif text-cream text-5xl sm:text-6xl lg:text-7xl tracking-tight">{n.name}</h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-3 gap-14">
          <div className="lg:col-span-2">
            <Reveal>
              <p className="text-lg text-[#525D6B] leading-relaxed">{n.description[lang]}</p>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-6 text-xs uppercase tracking-[0.25em] text-gold-dark font-semibold">{n.vibe[lang]}</p>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-10">
                <p className="font-serif text-2xl text-ink mb-5">{t.neighborhoodsPage.highlights}</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {n.highlights[lang].map((h) => (
                    <li key={h} className="flex items-start gap-3 text-[#525D6B] text-sm">
                      <Check className="w-4 h-4 text-gold shrink-0 mt-0.5" /> {h}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-1">
            <div className="bg-ink grain-overlay relative p-8 text-center lg:sticky lg:top-28">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">{t.neighborhoodsPage.median}</p>
              <p data-testid="neighborhood-median-price" className="mt-2 font-serif text-5xl text-cream">{n.median}</p>
              <div className="my-7 h-px bg-white/10" />
              <p className="font-serif text-cream text-xl">{t.neighborhoodsPage.ctaTitle}</p>
              <p className="mt-2 text-cream/60 text-sm leading-relaxed">{t.neighborhoodsPage.ctaSub}</p>
              <Link
                to="/contact"
                data-testid="neighborhood-ask-carlos-btn"
                className="group mt-6 inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-ink font-semibold text-sm px-7 py-3.5 rounded-full transition-colors duration-300"
              >
                {t.neighborhoodsPage.cta}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {localListings.length > 0 && (
        <section className="bg-white py-20 sm:py-28 border-t border-sand/60">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl text-ink mb-12">
                {t.neighborhoodsPage.listingsHere} {n.name}
              </h2>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {localListings.map((l, i) => (
                <PropertyCard key={l.id} listing={l} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
